import apiClient from '@shared/api/apiClient';

/**
 * 문서함 상태창  / 결재함 상태창 정보 요청
 */
async function getApprovalStatus() {
  const response = await apiClient.get('/v1/documents/stats', {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서함 요청
 *
 * @param {Object} params - 문서함 조회 파라미터
 * @param {string} params.type - 문서함 타입
 *  - ing : 상신함
 *  - return : 반려함
 *  - done : 기결
 *  - approvalIng : 미결
 *  - receive : 수신 : 결재완료만
 *  - futureIng : 예결
 *  - afterIng : 후결 : 없어짐
 *  - all : 관리자 문서조회
 *  - temDoc : 임시저장 문서조회
 *  - execute :  시행함 조회
 * @param {string} [params.date1] - 시작일자 (YYYYMMDD)
 * @param {string} [params.date2] - 종료일자 (YYYYMMDD)
 * @param {number} [params.stateCode] - 상태 코드
 * @param {number} [params.teamCode] - 팀 코드
 * @param {string} [params.name] - 작성자 이름
 * @param {string} [params.title] - 문서 제목
 * @param {number} [params.formCode] - 양식 코드
 * @param {number} [params.exeState] - 실행 상태
 */
async function getApprovalDocList({
  type,
  date1,
  date2,
  stateCode,
  teamCode,
  name,
  title,
  formCode,
  exeState,
}) {
  const response = await apiClient.post(
    `/v1/documents/list/${type}`,
    {
      date1,
      date2,
      stateCode,
      teamCode,
      name,
      title,
      formCode,
      exeState,
    },
    {
      withAuth: true,
    },
  );

  return response.data;
}
/**
 * 문서 (반려) 삭제
 * @param {number} idx : 문서 인덱스
 */
async function removeApprovalDoc(idx) {
  const response = await apiClient.put(
    `/v1/documents/${idx}/remove`,
    {},
    {
      withAuth: true,
    },
  );
  return response.data;
}
/**
 * 결재자 결재 처리
 * @param {number} signNumber : 결재자 번호 (1~5)
 * @param {number} idx : 문서 인덱스 필수
 * @param {string} comment : 결재 코멘트 선택
 */
async function postApprovalSign({ signNumber, idx, comment }) {
  const response = await apiClient.post(
    `/v1/approvals/done/sign${signNumber}`,
    {
      idx,
      ...(comment ? { comment } : {}),
    },
    { withAuth: true },
  );
  return response.data;
}
/**
 * 검토자 검토 처리
 * @param {number} checkNumber : 검토자 번호 (1~4)
 * @param {number} idx : 문서 인덱스 필수
 * @param {string} comment : 검토 코멘트 선택
 */
async function postApprovalReviewerDone({ checkNumber, idx, comment }) {
  const response = await apiClient.post(
    `/v1/approvals/done/check${checkNumber}`,
    {
      idx,
      ...(comment ? { comment } : {}),
    },
    { withAuth: true },
  );
  return response.data;
}

/**
 * 합의자 결재 처리
 * @param {number} idx : 문서 인덱스 필수
 * @param {string} comment : 결재 코멘트 선택
 */
async function postApprovalAgreementDone({ idx, comment }) {
  const response = await apiClient.post(
    '/v1/approvals/done/agreement',
    {
      idx,
      ...(comment ? { comment } : {}),
    },
    { withAuth: true },
  );
  return response.data;
}

/**
 * 전자문서 리스트 가져오기
 * POST /api/v1/documents/list/{type}
 * (PcController에서 DocumentController로 마이그레이션)
 *
 * @param {Object} params - 문서 조회 조건
 * @param {string} params.type - 문서함 타입
 *  - ing : 상신함
 *  - return : 반려함
 *  - done : 기결
 *  - approvalIng : 미결
 *  - receive : 수신 : 결재완료만
 *  - futureIng : 예결
 *  - afterIng : 후결 : 없어짐
 *  - all : 관리자 문서조회
 *  - temDoc : 임시저장 문서조회
 *  - execute :  시행함 조회
 * @param {string} params.date1 - 시작일(YYYYMMDD)
 * @param {string} params.date2 - 종료일(YYYYMMDD)
 * @param {number} params.stateCode - 상태 코드
 * @param {number} params.teamCode - 팀 코드
 * @param {string} params.userName - 작성자명
 * @param {string} params.title - 제목
 * @param {number} params.docType - 문서 종류 코드
 * @param {number} params.exeState - 실행 상태 코드
 */

async function getPcApprovalDocList(params) {
  const { type, ...restParams } = params;
  const response = await apiClient.post(`/v1/documents/list/${type}`, restParams, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 문서 시행 상태 변경
 * PUT /api/v1/documents/execute-state
 * (PcController에서 DocumentController로 마이그레이션)
 * @param {number[]} selectedIds - 시행 상태를 변경할 문서 ID 배열
 */
async function executeDocumentsState(selectedIds) {
  const response = await apiClient.put(
    '/v1/documents/execute-state',
    { docList: selectedIds },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 문서 참조 상태 변경
 * PUT /api/v1/documents/reference-state
 * (PcController에서 DocumentController로 마이그레이션)
 */
async function referenceDocumentState(selectedIds) {
  const response = await apiClient.put(
    '/v1/documents/reference-state',
    { docList: selectedIds },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 시행완료 메일발송 (단건)
 * POST /api/v1/documents/execution-complete-mail
 * - 시행함에서 완료된 문서 1건에 대해 "처리 완료" 메일을 발송
 * - 수신자: 문서작성자(상신자) + 메일발송 버튼을 누른 본인
 * @param {number} docIdx - 메일 발송 대상 문서 ID (단건)
 */
async function sendExecutionCompleteMail(docIdx) {
  const response = await apiClient.post(
    '/v1/documents/execution-complete-mail',
    { docIdx },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 결재 반려/수정요청 API
 * - 결재자/검토자/합의자가 문서를 반려하거나 수정요청할 때 사용
 * - API 경로: /v1/approvals/done/return/${pathPosition}
 * - type: 4=반려, 6=반려(수정)
 *
 * @param {number} docIdx - 문서 인덱스
 * @param {string} position - 결재 위치 (sign1~5, check1~4, agreement)
 * @param {number} type - 타입 (4:반려, 6:수정요청)
 * @param {string} comment - 반려/수정 사유
 * @returns {Promise<Object>} API 응답
 */
async function returnDocument(docIdx, position, type, comment) {
  // position에서 숫자 추출 (sign1 -> 1, check2 -> 2)
  // API 경로가 /sign{number}, /check{number}, /agreement 형태
  let pathPosition = '';
  if (position.startsWith('sign')) {
    const num = position.replace('sign', '');
    pathPosition = `sign${num}`;
  } else if (position.startsWith('check')) {
    const num = position.replace('check', '');
    pathPosition = `check${num}`;
  } else if (position === 'agreement') {
    pathPosition = 'agreement';
  } else {
    throw new Error(`잘못된 결재 위치입니다: ${position}`);
  }

  const response = await apiClient.put(
    `/v1/approvals/done/return/${pathPosition}`,
    {
      idx: String(docIdx), // 문서번호 (string)
      type, // 4:반려, 6:수정요청
      comment, // 반려/수정 사유 (string)
    },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 결재라인 서명 이미지 조회 API
 * GET /api/v1/approvals/sign-image/{docIdx}/{sign}
 * - 결재 완료한 사람의 서명 이미지를 조회
 *
 * @param {number} docIdx - 문서 인덱스
 * @param {string} sign - 결재 위치 (sign_1, sign_2, check_1, agreement 등)
 * @returns {Promise<Object>} 서명 이미지 데이터
 */
async function getApprovalSignImage(docIdx, sign) {
  const response = await apiClient.get(`/v1/approvals/sign-image/${docIdx}/${sign}`, {
    withAuth: true,
  });
  return response.data;
}

export {
  getApprovalStatus,
  getApprovalDocList,
  removeApprovalDoc,
  postApprovalSign,
  postApprovalReviewerDone,
  postApprovalAgreementDone,
  getPcApprovalDocList,
  executeDocumentsState,
  referenceDocumentState,
  sendExecutionCompleteMail,
  returnDocument,
  getApprovalSignImage,
};
